/**
 * progress — client feature module (spec §4.1, §4.7, brief §progress).
 * Mastery, streaks and "study this next" all come from `GET
 * /api/progress/overview`, computed server-side with `shared/mastery.ts`.
 */
import { BarChart3 } from "lucide-react";
import type { FeatureModule } from "@client/app/types";
import { ProgressPage } from "./Page";

const progressFeature: FeatureModule = {
  id: "progress",
  routes: [{ path: "/progress", Component: ProgressPage }],
  nav: [
    { to: "/progress", labelKey: "progress.nav", icon: BarChart3, order: 40, hideDuringExam: true },
  ],
  dict: {
    sv: {
      "progress.nav": "Framsteg",
      "progress.description":
        "Vad du klarar på egen hand, hur stor del av kursen du har täckt och hur väl din säkerhet stämmer med dina resultat.",
      "progress.stats.streak.label": "Studerat i rad",
      "progress.stats.streak.value.one": "{count} dag",
      "progress.stats.streak.value.other": "{count} dagar",
      "progress.stats.streak.longest.one": "Längsta: {count} dag",
      "progress.stats.streak.longest.other": "Längsta: {count} dagar",
      "progress.stats.autoAccuracy.label": "Andel rätt svar (automaträttade)",
      "progress.stats.exams.label": "Genomförda övningstentor",
      "progress.summary.started.label": "Påbörjade ämnen",
      "progress.summary.started.value": "{started} / {total}",
      "progress.summary.unaided.label": "Lösta på egen hand",
      "progress.summary.unaided.hint": "rätt utan ledtrådar eller hjälp",
      "progress.summary.assisted.label": "Lösta med hjälp",
      "progress.summary.assisted.hint": "rätt först efter ledtråd, lösning eller nytt försök",
      "progress.summary.retests.label": "Klarade repetitionsfrågor",
      "progress.summary.retests.value": "{passed} / {taken}",
      "progress.summary.retests.hint": "rätt utan hjälp minst en dag senare",
      "progress.howCounted.title": "Så räknas det",
      "progress.howCounted.body":
        "Varje uppgift räknas en gång: ditt första svar den senaste dagen du gjorde den. Svar efter ledtråd, lösning eller nytt försök väger hälften. Gamla svar väger inte mindre bara för att tiden går; de ersätts först när du gör uppgiften en senare dag.",
      "progress.map.title": "Kursöversikt",
      "progress.map.description":
        "Varje ruta är ett ämne. Färgen och symbolen visar vad du har klarat hittills.",
      "progress.map.instructions":
        "Använd piltangenterna för att flytta mellan ämnen och Enter för att visa detaljer.",
      "progress.map.legend": "Förklaring",
      "progress.map.state.not-started": "Inte påbörjat",
      "progress.map.state.tried": "Försökt",
      "progress.map.state.with-help": "Bara med hjälp",
      "progress.map.state.on-your-own": "På egen hand",
      "progress.map.state.secure": "Befäst",
      "progress.map.secureDefinition":
        "Befäst = rätt utan hjälp på en ny uppgift minst en dag senare.",
      "progress.map.tileLabel": "{topic}: {state}",
      "progress.map.detail.counts":
        "försökt {items} · på egen hand {unaided} · med hjälp {assisted}",
      "progress.map.detail.retestDue": "repetition {date}",
      "progress.map.detail.practise": "Öva på ämnet",
      "progress.map.detail.close": "Stäng",
      "progress.map.showTable": "Visa ämnestabell",
      "progress.studyNext.title": "Plugga härnäst",
      "progress.studyNext.empty": "Inget att rekommendera just nu — bra jobbat!",
      "progress.studyNext.reason.never-practised": "Aldrig övat",
      "progress.studyNext.reason.low-mastery": "Låg träffsäkerhet",
      "progress.studyNext.reason.stale": "Länge sedan sist",
      "progress.studyNext.cta": "Öva nu",
      "progress.flashcards.due.one": "{count} kort att repetera",
      "progress.flashcards.due.other": "{count} kort att repetera",
      "progress.flashcards.reviewsLast7d": "{count} repetitioner senaste 7 dagarna",
      "progress.chapters.title": "Momentöversikt",
      "progress.chapters.chapter": "Moment",
      "progress.chapters.notRated": "ej bedömd",
      "progress.chapters.masteryLabel": "Träffsäkerhet (uppskattad)",
      "progress.chapters.coverage": "{practised} av {total} ämnen övade",
      "progress.chapters.evidenceLabel": "Underlag",
      "progress.topics.table.topic": "Ämne",
      "progress.topics.table.mastery": "Träffsäkerhet",
      "progress.topics.table.attempts": "Försök",
      "progress.topics.table.state": "Läge",
      "progress.topics.table.lastPractised": "Senast övat",
      "progress.topics.table.trend": "Trend",
      "progress.topics.notRated": "ej bedömd",
      "progress.topics.neverPractised": "aldrig övat",
      "progress.trend.up": "Uppåtgående",
      "progress.trend.down": "Nedåtgående",
      "progress.trend.flat": "Stabil",
      "progress.calibration.title": "Säkerhet jämfört med resultat",
      "progress.calibration.axisX": "Hur säker du var",
      "progress.calibration.axisY": "Andel rätt",
      "progress.calibration.reference": "Perfekt kalibrering",
      "progress.calibration.chartLabel": "Diagram: andel rätt svar per säkerhetsnivå.",
      "progress.calibration.table.level": "Säkerhet",
      "progress.calibration.table.answers": "Svar",
      "progress.calibration.table.right": "Rätt",
      "progress.calibration.tooFew": "för få svar",
      "progress.calibration.caption.over":
        "Du är oftare säker än du har rätt — läs lösningen extra noga när du känt dig säker.",
      "progress.calibration.caption.under":
        "Du har oftare rätt än du tror — lita mer på ditt resonemang.",
      "progress.calibration.caption.good": "Din säkerhet stämmer väl med dina resultat.",
      "progress.calibration.caption.unclear":
        "Det går ännu inte att säga om din säkerhet stämmer med resultaten. Fortsätt skatta din säkerhet.",
      "progress.calibration.empty":
        "Ange hur säker du är på minst {min} svar för att se diagrammet.",
      "progress.calibration.scope":
        "Bygger på snabbfrågor och numeriska uppgifter, som rättas automatiskt.",
      "progress.calibration.series.problems": "Uppgifter",
      "progress.calibration.flashcardsScope":
        "Korten bedömer du själv efter att du har vänt dem. Därför visas de som en egen serie och påverkar inte texten ovan.",
      "progress.calibration.series.flashcards": "Kort",
      "progress.calibration.table.cardAnswers": "Kort",
      "progress.calibration.table.cardRight": "Kort rätt",
      "progress.slips.title": "Fel som återkommer",
      "progress.slips.window.one": "Räknat över den senaste dagen.",
      "progress.slips.window.other": "Räknat över de senaste {count} dagarna.",
      "progress.slips.kind.power-of-ten": "Fel tiopotens",
      "progress.slips.kind.unit": "Enheter",
      "progress.slips.kind.sign": "Fel tecken eller riktning",
      "progress.slips.kind.factor": "En faktor 2 eller 2π för mycket eller för lite",
      "progress.slips.kind.classical-for-relativistic":
        "Klassisk formel där en relativistisk behövs",
      "progress.slips.kind.wrong-principle": "Fel princip",
      "progress.slips.kind.plan-miss": "Valde fel princip i planeringen",
      "progress.slips.count.one": "{count} gång",
      "progress.slips.count.other": "{count} gånger",
      "progress.slips.practise.one": "Öva på dessa ({count} uppgift)",
      "progress.slips.practise.other": "Öva på dessa ({count} uppgifter)",
      "progress.slips.empty.one": "Inga återkommande fel den senaste dagen.",
      "progress.slips.empty.other": "Inga återkommande fel de senaste {count} dagarna.",
      "progress.weakest.title": "Svagaste ämnen",
      "progress.weakest.detail": "Uppskattad träffsäkerhet {percent} · {items} försökta uppgifter",
      "progress.weakest.cta": "Öva",
      "progress.weakest.ctaFor": "Öva på {topic}",
      "progress.weakest.empty.one":
        "Här hamnar de ämnen där det går sämst, när du har försökt minst {count} uppgift i ett ämne.",
      "progress.weakest.empty.other":
        "Här hamnar de ämnen där det går sämst, när du har försökt minst {count} uppgifter i ett ämne.",
      "progress.pace.title": "Ligger du rätt i tid?",
      "progress.pace.line":
        "Du har övat på {practised} av {taught} ämnen som föreläsningarna har tagit upp t.o.m. {id}.",
      "progress.pace.behind": "Öva på det vi gått igenom",
      "progress.pace.done": "Du ligger i fas.",
      "progress.comingBack.title": "Kommer tillbaka",
      "progress.comingBack.badge": "Utspridd repetition",
      "progress.comingBack.variant.sibling": "ny variant",
      "progress.comingBack.variant.new": "ny uppgift",
      "progress.comingBack.variant.seen": "en uppgift du har gjort förut",
      "progress.comingBack.cards.one": "{count} kort",
      "progress.comingBack.cards.other": "{count} kort",
      "progress.comingBack.today": "idag",
      "progress.comingBack.empty.one": "Inget planerat den närmaste dagen.",
      "progress.comingBack.empty.other": "Inget planerat de närmaste {count} dagarna.",
      "progress.activity.title": "Aktivitet senaste 30 dagarna",
      "progress.activity.attempts": "Frågor",
      "progress.activity.correctSuffix": "rätt",
      "progress.activity.reviews": "Repetitioner",
      "progress.activity.empty": "Ingen aktivitet ännu",
      "progress.exams.title": "Övningstentor",
      "progress.exams.empty": "Du har inte skrivit någon övningstenta ännu.",
      "progress.exams.kind.generated": "Slumpad övningstenta",
      "progress.exams.ungraded": "Inte rättad",
    },
    en: {
      "progress.nav": "Progress",
      "progress.description":
        "What you can do on your own, how much of the course you have covered and how well your confidence matches your results.",
      "progress.stats.streak.label": "Study streak",
      "progress.stats.streak.value.one": "{count} day",
      "progress.stats.streak.value.other": "{count} days",
      "progress.stats.streak.longest.one": "Longest: {count} day",
      "progress.stats.streak.longest.other": "Longest: {count} days",
      "progress.stats.autoAccuracy.label": "Share correct (auto-graded)",
      "progress.stats.exams.label": "Exams completed",
      "progress.summary.started.label": "Topics started",
      "progress.summary.started.value": "{started} / {total}",
      "progress.summary.unaided.label": "Solved on your own",
      "progress.summary.unaided.hint": "right without hints or help",
      "progress.summary.assisted.label": "Solved with help",
      "progress.summary.assisted.hint": "right only after a hint, a solution or a second try",
      "progress.summary.retests.label": "Re-tests passed",
      "progress.summary.retests.value": "{passed} / {taken}",
      "progress.summary.retests.hint": "right without help at least a day later",
      "progress.howCounted.title": "How this is counted",
      "progress.howCounted.body":
        "Each problem counts once: your first answer on the most recent day you did it. An answer after a hint, a solution or a second try counts half. Older answers do not weigh less just because time passes; they are replaced only when you do the problem on a later day.",
      "progress.map.title": "Course map",
      "progress.map.description":
        "Each square is a topic. The colour and the symbol show what you have managed so far.",
      "progress.map.instructions":
        "Use the arrow keys to move between topics and Enter to show details.",
      "progress.map.legend": "Legend",
      "progress.map.state.not-started": "Not started",
      "progress.map.state.tried": "Tried",
      "progress.map.state.with-help": "With help only",
      "progress.map.state.on-your-own": "On your own",
      "progress.map.state.secure": "Secure",
      "progress.map.secureDefinition":
        "Secure = right without help on a new problem at least one day later.",
      "progress.map.tileLabel": "{topic}: {state}",
      "progress.map.detail.counts": "tried {items} · on your own {unaided} · with help {assisted}",
      "progress.map.detail.retestDue": "re-test due {date}",
      "progress.map.detail.practise": "Practise this topic",
      "progress.map.detail.close": "Close",
      "progress.map.showTable": "Show topic table",
      "progress.studyNext.title": "Study next",
      "progress.studyNext.empty": "Nothing to recommend right now — nice work!",
      "progress.studyNext.reason.never-practised": "Never practised",
      "progress.studyNext.reason.low-mastery": "Low estimated accuracy",
      "progress.studyNext.reason.stale": "Long time no practice",
      "progress.studyNext.cta": "Practice now",
      "progress.flashcards.due.one": "{count} card due",
      "progress.flashcards.due.other": "{count} cards due",
      "progress.flashcards.reviewsLast7d": "{count} reviews in the last 7 days",
      "progress.chapters.title": "Module overview",
      "progress.chapters.chapter": "Module",
      "progress.chapters.notRated": "not yet rated",
      "progress.chapters.masteryLabel": "Estimated accuracy",
      "progress.chapters.coverage": "{practised} of {total} topics practised",
      "progress.chapters.evidenceLabel": "Amount of evidence",
      "progress.topics.table.topic": "Topic",
      "progress.topics.table.mastery": "Estimated accuracy",
      "progress.topics.table.attempts": "Attempts",
      "progress.topics.table.state": "State",
      "progress.topics.table.lastPractised": "Last practised",
      "progress.topics.table.trend": "Trend",
      "progress.topics.notRated": "not yet rated",
      "progress.topics.neverPractised": "never practised",
      "progress.trend.up": "Trending up",
      "progress.trend.down": "Trending down",
      "progress.trend.flat": "Steady",
      "progress.calibration.title": "Confidence compared with results",
      "progress.calibration.axisX": "How sure you were",
      "progress.calibration.axisY": "Share right",
      "progress.calibration.reference": "Perfect calibration",
      "progress.calibration.chartLabel": "Chart: share of right answers per confidence level.",
      "progress.calibration.table.level": "Confidence",
      "progress.calibration.table.answers": "Answers",
      "progress.calibration.table.right": "Right",
      "progress.calibration.tooFew": "too few answers",
      "progress.calibration.caption.over":
        "You are more often certain than you are right — read the solution extra carefully when you felt sure.",
      "progress.calibration.caption.under":
        "You are more often right than you think — trust your reasoning more.",
      "progress.calibration.caption.good": "Your confidence matches your results well.",
      "progress.calibration.caption.unclear":
        "It isn't possible yet to say whether your confidence matches your results. Keep rating your confidence.",
      "progress.calibration.empty":
        "Rate how sure you are on at least {min} answers to see this chart.",
      "progress.calibration.scope":
        "Based on quick checks and numeric problems, which are graded automatically.",
      "progress.calibration.series.problems": "Problems",
      "progress.calibration.flashcardsScope":
        "You grade the cards yourself after flipping them, so they are shown as a separate series and do not affect the text above.",
      "progress.calibration.series.flashcards": "Flashcards",
      "progress.calibration.table.cardAnswers": "Cards",
      "progress.calibration.table.cardRight": "Cards right",
      "progress.slips.title": "Your recurring slips",
      "progress.slips.window.one": "Counted over the last day.",
      "progress.slips.window.other": "Counted over the last {count} days.",
      "progress.slips.kind.power-of-ten": "Power of ten",
      "progress.slips.kind.unit": "Units",
      "progress.slips.kind.sign": "Wrong sign or direction",
      "progress.slips.kind.factor": "A factor of 2 or 2π too many or too few",
      "progress.slips.kind.classical-for-relativistic":
        "Classical formula where a relativistic one is needed",
      "progress.slips.kind.wrong-principle": "Wrong principle",
      "progress.slips.kind.plan-miss": "Named the wrong principle in the plan",
      "progress.slips.count.one": "once",
      "progress.slips.count.other": "{count} times",
      "progress.slips.practise.one": "Practise these ({count} problem)",
      "progress.slips.practise.other": "Practise these ({count} problems)",
      "progress.slips.empty.one": "No recurring slips in the last day.",
      "progress.slips.empty.other": "No recurring slips in the last {count} days.",
      "progress.weakest.title": "Weakest topics",
      "progress.weakest.detail": "Estimated accuracy {percent} · {items} problems tried",
      "progress.weakest.cta": "Practise",
      "progress.weakest.ctaFor": "Practise {topic}",
      "progress.weakest.empty.one":
        "Your weakest topics show up here once you have tried at least {count} problem in a topic.",
      "progress.weakest.empty.other":
        "Your weakest topics show up here once you have tried at least {count} problems in a topic.",
      "progress.pace.title": "Are you on schedule?",
      "progress.pace.line":
        "You have practised {practised} of the {taught} topics the lectures have covered through {id}.",
      "progress.pace.behind": "Practise what we have covered",
      "progress.pace.done": "You are on schedule.",
      "progress.comingBack.title": "Coming back",
      "progress.comingBack.badge": "Spaced re-test",
      "progress.comingBack.variant.sibling": "new variant",
      "progress.comingBack.variant.new": "new problem",
      "progress.comingBack.variant.seen": "a problem you have done before",
      "progress.comingBack.cards.one": "{count} card",
      "progress.comingBack.cards.other": "{count} cards",
      "progress.comingBack.today": "today",
      "progress.comingBack.empty.one": "Nothing scheduled for the next day.",
      "progress.comingBack.empty.other": "Nothing scheduled for the next {count} days.",
      "progress.activity.title": "Activity, last 30 days",
      "progress.activity.attempts": "Questions",
      "progress.activity.correctSuffix": "correct",
      "progress.activity.reviews": "Reviews",
      "progress.activity.empty": "No activity yet",
      "progress.exams.title": "Exam history",
      "progress.exams.empty": "No exams completed yet",
      "progress.exams.kind.generated": "Generated exam",
      "progress.exams.ungraded": "Not graded",
    },
  },
};

export default progressFeature;
